import ctl from "@netlify/classnames-template-literals";

export type Variant =
  | "default"
  | "secondary"
  | "tertiary"
  | "ghost"
  | "darkglass"
  | "glass"
  | "dark"
  | "transparent";

export const getDisclosureWrapper = (variant: Variant) =>
  ctl(`
  relative
  w-full
  rounded-b-2xl
  mb-1
  ${variant === "secondary" ? "border border-neutral-200" : ""}
  ${variant === "transparent" ? "group" : ""}
`);

export const getButtonWrapper = (
  variant: Variant,
  isOpen: boolean,
  disabledCursor: "" | "cursor-not-allowed" | "cursor-auto",
  chevronIconPosition: "left" | "right",
  chevronVerticalAlign: "top" | "center",
  fullWidth?: boolean,
  padding?: "none" | "sm" | "md" | "lg",
  showBorderRadius?: boolean,
) =>
  ctl(`
  flex
  ${chevronIconPosition === "right" ? "justify-between" : ""}
  w-full
  rounded-xl
  peer
  outline-none
  transition-colors
  ${chevronVerticalAlign === "center" ? "items-center" : "items-start"}
  relative
  z-10
  ${padding === "none" ? "p-0" : ""}
  ${padding === "sm" ? "p-2" : ""}
  ${padding === "md" ? "p-4" : ""}
  ${padding === "lg" ? "p-6" : ""}
  
  ${variant === "glass" ? "transition-all" : ""}
  ${variant === "glass" && isOpen && "bg-gradient-to-br from-status-teal-500/30 to-status-teal-50/30 hover:from-status-teal-500/50 hover:to-status-teal-50/50"}
  ${variant === "glass" && !isOpen && "bg-gradient-to-br from-purple-500/30 to-purple-300/30 hover:from-purple-500/50 hover:to-purple-300/50"}
  ${variant !== "ghost" && variant !== "glass" && variant !== "dark" ? "bg-neutral-50" : ""}
  ${variant === "tertiary" ? "!bg-neutral-50" : ""}
  ${variant === "darkglass" ? "!bg-[#1C1831]/50 text-white" : ""}
  ${variant === "dark" && !isOpen ? "!bg-neutral-800/30 text-white" : ""}
  ${variant === "dark" && isOpen ? "!bg-neutral-850 text-white" : ""}
  ${variant === "transparent" && !isOpen ? "!bg-transparent text-white hover:!bg-neutral-700 active:!bg-neutral-600 focus:!border-white" : ""}
  ${variant === "transparent" && isOpen ? "text-white focus:!border-white pb-2" : ""}
  ${
    variant === "secondary" ||
    variant === "ghost" ||
    variant === "glass" ||
    variant === "darkglass" ||
    variant === "dark"
      ? ""
      : "hover:!bg-neutral-50"
  }
  ${
    isOpen &&
    variant !== "secondary" &&
    variant !== "darkglass" &&
    variant !== "ghost" &&
    variant !== "glass" &&
    variant !== "dark" &&
    variant !== "transparent"
      ? "!bg-neutral-50"
      : ""
  }
  ${isOpen && variant === "transparent" ? "!bg-neutral-600 group-hover:!bg-neutral-700" : ""}

  ${showBorderRadius ? "rounded-b-2xl" : "rounded-b-none"}
  ${disabledCursor}
  ${fullWidth ? "px-0" : ""}
`);

export const getContentWrapper = (variant: Variant, isVisible = true) =>
  ctl(`
  rounded-b-2xl
  transition-colors
  ${
    variant === "secondary" || variant === "ghost" || variant === "dark"
      ? ""
      : "bg-neutral-50 peer-active:!bg-neutral-100"
  }
  ${variant === "darkglass" && isVisible ? "!bg-neutral-900/50 peer-active:!bg-neutral-900/50 text-white" : ""}
  ${variant === "darkglass" && !isVisible ? "!bg-transparent text-white" : ""}
  ${variant === "dark" && isVisible ? "!bg-neutral-850 peer-active:!bg-neutral-850 text-white" : ""}
  ${variant === "dark" && !isVisible ? "!bg-transparent text-white" : ""}
  ${variant === "transparent" ? "!bg-transparent text-white peer-active:!bg-neutral-600" : ""}
`);

export type ContentPadding = "none" | "sm" | "md" | "lg";

const contentPaddingClasses: Record<ContentPadding, string> = {
  none: "p-0",
  sm: "p-2",
  md: "p-4",
  lg: "p-6",
};

export const getContentInnerWrapper = (
  variant: Variant,
  fullWidth?: boolean,
  contentPadding?: ContentPadding,
) => {
  if (variant === "secondary") {
    return ctl(`
        bg-neutral-50
        ${contentPadding ? contentPaddingClasses[contentPadding] : `${fullWidth ? "" : "px-6"} pb-6`}
    `);
  }

  if (variant === "tertiary") {
    return ctl(`
      ${contentPadding ? contentPaddingClasses[contentPadding] : `${fullWidth ? "" : "px-6"} pb-6 pt-4`}
  `);
  }

  if (variant === "darkglass") {
    return ctl(`
      ${contentPadding ? contentPaddingClasses[contentPadding] : `${fullWidth ? "" : "px-4"} pb-4 pt-0`}
  `);
  }

  if (variant === "dark") {
    return ctl(`
      bg-neutral-850
      rounded-b-xl
      ${contentPadding ? contentPaddingClasses[contentPadding] : "p-3 pt-2"}
  `);
  }
  if (variant === "transparent") {
    return ctl(`
        bg-neutral-600
        group-hover:bg-neutral-700
        rounded-b-xl
        ${contentPadding ? contentPaddingClasses[contentPadding] : ""}
    `);
  }

  if (variant !== "ghost") {
    return ctl(`
      ${contentPadding ? contentPaddingClasses[contentPadding] : `${fullWidth ? "" : "px-4"} py-4`}
  `);
  }
};

export const getContentChildrenWrapper = (variant: Variant) =>
  ctl(`
    ${variant === "secondary" ? "mt-6" : ""}
    ${variant === "darkglass" ? "pt-4" : ""}
    ${variant === "transparent" ? "p-4 pt-0 text-neutral-200" : ""}
`);

export const header = ctl(`
  flex
  items-center
  justify-start
  text-left
`);

export const getChevronStyle = (isOpen: boolean) =>
  ctl(`
  ${isOpen ? "-rotate-180" : "rotate-0"}
  transform
  transition-all
`);
